import { memo, useEffect, useState } from "react";
import { MiAutocomplete, MiButton } from "@miplatform/components";
import { BUTTON_COLORS } from "@miplatform/constants";
import { useModal } from "@miplatform/hooks";
import { isEmpty } from "@miplatform/utils";
import { MenuItem, Paper, Popper, TextField, Typography } from "@mui/material";
import { AddPurchaseOrderModal } from "../../../views/Accounting/Reconcile/ReconcileDocumentsSection/AddPurchaseOrderModal/AddPurchaseOrderModal";

export const PurchaseOrderAutocomplete = ({
  homeId,
  value,
  orderTemplate,
  purchaseOrders,
  onChange,
  onCreate = () => {},
  isReadOnly,
  required,
  ...props
}) => {
  const {
    isModalOpen: isAddPurchaseOrderModalOpen,
    openModal: openAddPurchaseOrderModal,
    closeModal: closeAddPurchaseOrderModal,
  } = useModal();

  const {
    isModalOpen: isAutocompleteOpen,
    openModal: openAutocomplete,
    closeModal: closeAutocomplete,
  } = useModal();

  const [search, setSearch] = useState("");

  const handleSearch = (_e, value) => setSearch(value);

  const handleClear = () => {
    setSearch("");
    onChange({});
  };

  const handleCreate = (purchaseOrder) => {
    onCreate(purchaseOrder);
    setSearch(purchaseOrder.number);
    closeAddPurchaseOrderModal();
  };

  const getOptionString = ({ number, openBalance }) =>
    `${number} - Open Balance: $${openBalance}`;

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  return (
    <>
      <MiAutocomplete
        size="small"
        fullWidth
        freeSolo={isEmpty(value)}
        forcePopupIcon={false}
        handleClear={handleClear}
        open={isAutocompleteOpen}
        options={purchaseOrders}
        value={value}
        inputValue={search}
        filterOptions={(options) =>
          options.filter((option) =>
            getOptionString(option).toLowerCase().includes(search.toLowerCase())
          )
        }
        getOptionLabel={(value) =>
          typeof value === "string"
            ? value
            : purchaseOrders.find(({ number }) => number === value.number)
                .number
        }
        readOnly={isReadOnly}
        onInputChange={handleSearch}
        isOptionEqualToValue={(value, option) =>
          typeof value === "string"
            ? value === option?.number
            : option?.id === value?.id
        }
        onChange={(_e, value) => onChange(value)}
        onOpen={openAutocomplete}
        onClose={closeAutocomplete}
        renderOption={({ ...props }, { id, number, openBalance }) => (
          <MenuItem key={id} {...props}>
            <Typography
              lineHeight={2}
            >{`${number} - Open Balance: $${openBalance}`}</Typography>
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            shrunk
            label="Purchase Order Number"
            InputProps={{
              ...params.InputProps,
              value: search,
            }}
          />
        )}
        slotProps={{
          paper: {
            onMouseDown: openAddPurchaseOrderModal,
          },
        }}
        PopperComponent={AutocompletePopper}
        PaperComponent={PurchaseOrderAutocompletePaper}
        {...props}
      />
      <AddPurchaseOrderModal
        homeId={homeId}
        orderTemplates={[orderTemplate]}
        open={isAddPurchaseOrderModalOpen}
        onClose={closeAddPurchaseOrderModal}
        onCreate={handleCreate}
      />
    </>
  );
};

export const PurchaseOrderAutocompletePaper = memo(
  ({ children, onMouseDown, ...props }) => (
    <Paper {...props}>
      {children}
      <MiButton
        fullWidth
        title="New Purchase Order"
        color={BUTTON_COLORS.BLUE}
        sx={{
          borderRadius: 0,
          justifyContent: "flex-start",
          color: "blue.main",
          pl: 4,
        }}
        onMouseDown={onMouseDown}
      />
    </Paper>
  )
);

const AutocompletePopper = memo((props) => (
  <Popper {...props} placement="bottom-start" />
));
